"use client"

import React, { createContext, useContext, useEffect, useState } from "react"
import { Language, translations } from "@/lib/i18n"

type LanguageContextType = {
  language: Language
  t: typeof translations.en
  setLanguage: (lang: Language) => void
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>("en")

  useEffect(() => {
    const savedLang = localStorage.getItem("preferred-language") as Language
    
    if (savedLang && (savedLang === "en" || savedLang === "es")) {
      setLanguage(savedLang)
    } else {
      // 2. Detect browser language
      const browserLang = navigator.language.split("-")[0]
      if (browserLang === "es") {
        setLanguage("es")
      } else {
        setLanguage("en")
      }
    }
  }, [])

  useEffect(() => {
    document.documentElement.lang = language
    localStorage.setItem("preferred-language", language)
  }, [language])

  const value = {
    language,
    t: translations[language],
    setLanguage
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}
